/**
 * Sweet state store for processed comments data.
 * This store should ONLY be updated by CommentsDataBackgroundLoader.
 * All other consumers should use the state/actions hooks to read data.
 */

import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';
import type { BoundActions, StoreActionApi } from 'react-sweet-state';

import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import type { Comment, CommentReply, RefetchOptions } from './useAllCommentsData.ts';
import {
	categorizeComments,
	buildDefaultCommentsContentStatusFilters,
} from './commentDataManagerUtils.ts';
import type { CommentsDataMap } from './commentDataManagerUtils.ts';
import type { useAllCommentsDataQuery$data } from './__generated__/useAllCommentsDataQuery.graphql.ts';

/**
 * State type for processed comments data.
 * This represents the categorized and filtered comments data ready for consumption.
 */
export type ProcessedCommentsDataState = {
	lastProcessedData: useAllCommentsDataQuery$data | null; // Track last processed data reference to detect when data is unchanged (cached)
	allComments: Comment[];
	allFilteredComments: Comment[];
	openInlineComments: Comment[];
	openFooterComments: Comment[];
	resolvedComments: Comment[];
	openComments: Comment[];
	commentsDataMap: CommentsDataMap;
	flatCommentsMap: Record<string, Comment | CommentReply>;
	flatMarkerRefMap: Record<string, Comment>;
	hasNestedComments: boolean;
	isDataProcessed: boolean;
	commentCount: number | null;
	hasAnyComments: boolean;
	error: Error | undefined;
	refetch: ((options?: RefetchOptions) => Promise<any>) | undefined;
};

/**
 * Returns the initial state for the processed comments data store.
 */
export const getInitialState = (): ProcessedCommentsDataState => ({
	lastProcessedData: null,
	allComments: [],
	allFilteredComments: [],
	openInlineComments: [],
	openFooterComments: [],
	resolvedComments: [],
	openComments: [],
	commentsDataMap: { inline: {}, general: {} },
	flatCommentsMap: {},
	flatMarkerRefMap: {},
	hasNestedComments: false,
	isDataProcessed: false,
	commentCount: 0,
	hasAnyComments: false,
	error: undefined,
	refetch: undefined,
});

/**
 * Actions for managing processed comments data.
 * NOTE: setProcessedData should ONLY be called by CommentsDataBackgroundLoader.
 */
export const actions = {
	/**
	 * Updates all processed comments data.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 * Note: refetch is set separately via setRefetch, so it's not included here.
	 */
	setProcessedData:
		(data: Omit<ProcessedCommentsDataState, 'error' | 'refetch'>) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...data,
				error: undefined,
				// Keep existing refetch if it exists, don't overwrite it
				refetch: currentState.refetch,
			});
		},

	/**
	 * Sets an error state for processed comments data.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	setError:
		(error: Error) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...currentState,
				error,
				isDataProcessed: true,
			});
		},

	/**
	 * Clears error and resets isDataProcessed to initial values.
	 * Skips setState when already at initial values to avoid unnecessary re-renders.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	clearErrorAndResetIsDataProcessed:
		() =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			const initialState = getInitialState();
			if (
				currentState.error === initialState.error &&
				currentState.isDataProcessed === initialState.isDataProcessed
			) {
				return;
			}
			setState({
				...currentState,
				error: initialState.error,
				isDataProcessed: initialState.isDataProcessed,
			});
		},

	/**
	 * Sets isDataProcessed to true without changing other state.
	 * Only updates if isDataProcessed is currently false to prevent unnecessary re-renders.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	setDataToProcessed:
		() =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			if (currentState.isDataProcessed) {
				return;
			}
			setState({
				...currentState,
				isDataProcessed: true,
			});
		},

	/**
	 * Sets the refetch function from useAllCommentsData.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	setRefetch:
		(refetch: (options?: RefetchOptions) => Promise<any>) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...currentState,
				refetch,
			});
		},

	/**
	 * Clears all processed comments data and resets to initial state.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	clearProcessedData:
		() =>
		({ setState }: StoreActionApi<ProcessedCommentsDataState>) => {
			setState(getInitialState());
		},

	/**
	 * Processes the raw comments data if it hasn't already been processed and updates the store with
	 * categorized comments. Can be used with output from useAllCommentsData() hook (that is, the full
	 * comments data query result) or the full comments data cache (for when we need to synchronously
	 * process updated cache data).
	 *
	 * Note that for Apollo GQL, if the data hasn't changed then readQuery() will return the same
	 * data reference as the full comment query does. So it is safe to compare data references from
	 * real queries and cache reads to avoid unnecessary reprocessing.
	 *
	 */
	// TODO: Verify the data reference behavior for Relay GQL when we start enabling Relay support.
	processCommentsData:
		({ data, pageMode }: { data: useAllCommentsDataQuery$data | null; pageMode: PageMode }) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			// Nothing to process if data is null
			if (!data) {
				return;
			}

			// Don't process the same data twice - if the data reference is the same as the last processed
			const currentState = getState();
			if (currentState.isDataProcessed && currentState.lastProcessedData === data) {
				return;
			}

			// Filter out null nodes and create array (empty array if no data)
			const allComments = (data?.comments?.nodes || []).filter(
				(node: Comment | null): node is Comment => node !== null,
			);

			// Categorize comments using default contentStatus filters
			const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);
			const categorizedData = categorizeComments(allComments, contentStatusFilters);

			// Update the store with categorized comments
			setState({
				...currentState,
				...categorizedData,
				lastProcessedData: data,
				error: undefined,
				isDataProcessed: true,
			});
		},
};

/**
 * Sweet state store for processed comments data.
 * This is the single source of truth for processed/categorized comments.
 */
export const ProcessedCommentsDataStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'ProcessedCommentsDataStore',
});

/**
 * Hook to access both state and actions for processed comments data.
 * Most consumers should use useProcessedCommentsDataState instead.
 */
export const useProcessedCommentsData = createHook(ProcessedCommentsDataStore);

/**
 * Hook to access actions for processed comments data.
 * WARNING: These actions should ONLY be used by CommentsDataBackgroundLoader.
 */
export const useProcessedCommentsDataActions = createActionsHook(ProcessedCommentsDataStore);

/**
 * Hook to access state for processed comments data.
 * This is the recommended hook for most consumers.
 */
export const useProcessedCommentsDataState = createStateHook(ProcessedCommentsDataStore);

// Non-React utility for accessing store outside of React components
const processedCommentsDataStoreInstance = defaultRegistry.getStore(ProcessedCommentsDataStore);

/**
 * Gets the current processed comments data state outside of React components.
 * This function can be called from anywhere to access the current state.
 * Uses the store instance from defaultRegistry to get the latest state.
 */
export const getProcessedCommentsDataState = (): ProcessedCommentsDataState => {
	return processedCommentsDataStoreInstance.storeState.getState();
};

/**
 * Gives access to the processed comments data actions outside of React components.
 * This function can be called from anywhere to access the actions.
 * Uses the store instance from defaultRegistry to get the actions.
 */
export const getProcessedCommentsDataActions = (): BoundActions<
	ProcessedCommentsDataState,
	typeof actions
> => {
	return processedCommentsDataStoreInstance.actions;
};
